/**
 * `ActiveFilter` — the shared Active/Inactive/All toggle every v2 config list
 * mounts (DNKADM-0049, handoff §4.3). A small MUI `ToggleButtonGroup` (the same
 * idiom as the notifications status filter) that drives the `useActiveFilter`
 * state; it does NOT fetch — the consuming list re-runs its `useResource` on the
 * value change. Default selection is **Active** (live rows only).
 */
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import type { ActiveFilterValue } from '@/hooks/useActiveFilter';

export interface ActiveFilterProps {
  /** The current selection (from `useActiveFilter`). */
  value: ActiveFilterValue;
  /** Called with the next value when the user picks a different option. */
  onChange: (next: ActiveFilterValue) => void;
  /** Accessible group label; defaults to a generic "Filter by status". */
  'aria-label'?: string;
}

/** Active / Inactive / All segmented control for a config list. */
export function ActiveFilter({
  value,
  onChange,
  'aria-label': ariaLabel = 'Filter by status',
}: ActiveFilterProps) {
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      onChange={(_e, next: ActiveFilterValue | null) => {
        // `exclusive` emits null when the active button is re-clicked; ignore it
        // so the filter always has a value (a list must show SOMETHING).
        if (next !== null) onChange(next);
      }}
      aria-label={ariaLabel}
    >
      <ToggleButton value="active" sx={{ textTransform: 'none', px: 1.25, py: 0.25 }}>
        Active
      </ToggleButton>
      <ToggleButton value="inactive" sx={{ textTransform: 'none', px: 1.25, py: 0.25 }}>
        Inactive
      </ToggleButton>
      <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.25, py: 0.25 }}>
        All
      </ToggleButton>
    </ToggleButtonGroup>
  );
}
